import store from '@/store'
import { Action, config, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { Hub } from "@aws-amplify/core";
import { API, Auth } from "aws-amplify";
import { Vue } from "vue-property-decorator";
import _ from "lodash";

config.rawError = true

const name = 'users';
if (module.hot) {
    if (store.hasModule(name)) {
        store.unregisterModule(name);
    }
}

const API_NAME = "Viewpoint360API";

@Module({dynamic: true, store: store, name: name, namespaced: true})
export default class Users extends VuexModule {

    userProfiles: any = {};

    //Current user credentials from Cognito
    currentAuthUser: any = null;

    // Current user membership
    currentUserMembership: any = null;

    // User profile information
    currentUserProfile: any = null;

    outgoingFetchCalls: any = {};

    @Mutation
    setCurrentAuthUser(currentAuthUser: any) {
        this.currentAuthUser = currentAuthUser;
    }

    @Mutation
    setCurrentUserMembership(membership: any) {
        this.currentUserMembership = membership;
    }

    @Mutation
    setCurrentUserProfile(currentUserProfile: any) {
        this.currentUserProfile = currentUserProfile;
    }

    @Mutation
    setUserProfiles(payload?: { [userId: string]: any }) {
        if (payload) {
            for (let uId in payload) {
                Vue.set(this.userProfiles, uId, payload[uId]);
            }
        } else {
            this.userProfiles = {};
        }
    }

    @Mutation
    removeUserProfile(uId: string) {
        if (uId) {
            Vue.delete(this.userProfiles, uId);
        }
    }

    @Mutation
    clearUsers() {
        this.userProfiles = {};
        this.currentAuthUser = null;
        this.currentUserMembership = null;
        this.currentUserProfile = null;
    }

    @Mutation
    setOutgoingFetch(payload: { key: string, value: boolean }) {
        Vue.set(this.outgoingFetchCalls, payload.key, payload.value);
    }

    @Action
    async fetchCurrentUserData() {
        if (this.outgoingFetchCalls['CURRENT']) return {};

        this.setOutgoingFetch({key: 'CURRENT', value: true})

        let currentAuthUser = await Auth.currentAuthenticatedUser()
        this.setCurrentAuthUser(currentAuthUser);

        let userId = currentAuthUser.username;
        let userProfileAsync = API.get(API_NAME, `/users/${userId}/profile`, {});
        let userMembershipAsync = API.get(API_NAME, `/users/${userId}/membership`, {})

        let userProfile = await userProfileAsync;
        this.setCurrentUserProfile(userProfile);

        let userMembership = await userMembershipAsync
        this.setCurrentUserMembership(userMembership);

        this.setOutgoingFetch({key: 'CURRENT', value: false})

        return {userProfile: userProfile, userAuth: currentAuthUser}
    }

    @Action
    async getCurrentAuthUser(bypassCache?: boolean) {
        if (bypassCache) {
            let currentAuthUser = await Auth.currentAuthenticatedUser({bypassCache: true})
            this.setCurrentAuthUser(currentAuthUser);
        }
        
        if (this.currentAuthUser) return this.currentAuthUser;

        let currentAuthUser = await Auth.currentAuthenticatedUser()
        this.setCurrentAuthUser(currentAuthUser);
        return currentAuthUser;

    }

    @Action
    async getCurrentUserMembership() {
        if (this.currentUserMembership) return this.currentUserMembership;
        let userId = this.currentAuthUser.username;
        let userMembership = await API.get(API_NAME, `/users/${userId}/membership`, {})
        this.setCurrentUserMembership(userMembership);
        return userMembership;
    }

    @Action
    async getUserProfile(userId: string) {
        if (this.userProfiles[userId]) {
            return this.userProfiles[userId];
        }
        return (await this.fetchCurrentUserData()).userProfile;
    }

    @Action
    async getUserProfileByEmail(email: string) {
        let init = {
            queryStringParameters: {
                email: email
            }
        }
        let profiles = await API.get(API_NAME, `/users/profile`, init);
        if (Object.keys(profiles).length > 0) {
            return Object.values(profiles)[0];
        }
        return null;
    }

    @Action
    async updateUserProfile(userProfile: any) {
        let myInit = {
            body: {
                first: (userProfile.first ? userProfile.first : ''),
                last: (userProfile.last ? userProfile.last : ''),
                image: (userProfile.image ? userProfile.image : ''),
                phone: (userProfile.phone ? userProfile.phone : ''),
            }
        };
        let updatedProfile = await API.post(API_NAME, `/users/${userProfile.userId}/profile`, myInit);
        this.setCurrentUserProfile(userProfile);
        return updatedProfile;
    }

    @Action
    async getSelectedDecisionUser(userId: string) {
        if (this.userProfiles[userId]) {
            return this.userProfiles[userId];
        } else {
            let decisionUsers = await this.getSelectedDecisionUsers()
            return decisionUsers[userId];
        }
    }

    @Action
    async getSelectedDecisionUsers() {
        let selectedDecision = this.context.rootGetters['decisions/selectedDecision'];
        this.setUserProfiles();
        if (selectedDecision) {
            if (this.outgoingFetchCalls[selectedDecision.teamId]) return {};
            this.setOutgoingFetch({key: selectedDecision.teamId, value: true});
            let users: any[] = selectedDecision.adminUsers.concat(selectedDecision.users);
            let userProfiles = await this.fetchUserProfiles(users);
            this.setUserProfiles(_.keyBy(userProfiles, 'userId'));
            this.setOutgoingFetch({key: selectedDecision.teamId, value: false})
            return userProfiles;
        }
        return {};
    }

    @Action
    async fetchUserProfiles(userIds: string[]) {
        let filteredUserIds = userIds.filter(uId => !this.outgoingFetchCalls[uId]);
        filteredUserIds.forEach(userId => this.setOutgoingFetch({key: userId, value: true}));
        let req = {
            queryStringParameters: {
                userIds: filteredUserIds
            }
        }
        if (!filteredUserIds.length) return;
        let userProfiles = await API.get(API_NAME, `/users/profile`, req);
        this.setUserProfiles(_.keyBy(userProfiles, 'userId'));
        filteredUserIds.forEach(userId => this.setOutgoingFetch({key: userId, value: false}));
        return userProfiles;
    }

    @Action
    async searchUsersByEmailPrefix(email: string) {
        let init = {
            queryStringParameters: {
                email: email
            }
        }
        return await API.get(API_NAME, `/users/search`, init);
    }

    @Action
    async cancelMembership() {
        let userId = this.currentUserProfile.userId
        await API.post(API_NAME, `/billing/${userId}/membership/cancel`, {});
        await this.fetchCurrentUserData()
    }

    @Action
    async resumeMembership() {
        let userId = this.currentUserProfile.userId
        await API.post(API_NAME, `/billing/${userId}/membership/resume`, {});
        await this.fetchCurrentUserData()
    }

    @Action
    async signOut() {
        this.clearUsers();
    }
}

const usersModule = getModule(Users);
Hub.listen('auth', (data) => {
    if (data.payload.event === 'signIn') {
        usersModule.fetchCurrentUserData();
    } else if (data.payload.event === 'signOut') {
        usersModule.signOut()
    }
});